import React, { useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils'; // For Tailwind class merging

// Define types for your rule condition and available fields/operators
export interface RuleCondition {
  id: string; // Unique ID for the condition row
  field: string; // e.g., 'order.subtotal', 'customer.is_loyalty'
  operator: string; // e.g., 'equals', 'greater_than', 'is_true'
  value: string | number | boolean | Date | string[] | number[] | null | undefined; // The actual value, can be string, number, boolean, date, or array
}

interface RuleConditionRowProps {
  condition: RuleCondition;
  onUpdate: (updatedCondition: RuleCondition) => void;
  onRemove: (id: string) => void;
  availableFields: { label: string; value: string; type: 'text' | 'number' | 'boolean' | 'timestamp' | 'array_text' | 'jsonb'; table: 'order' | 'customer' }[];
  availableOperators: { [key: string]: { label: string; value: string; compatibleTypes?: string[] }[] };
}

const RuleConditionRow: React.FC<RuleConditionRowProps> = ({
  condition,
  onUpdate,
  onRemove,
  availableFields,
  availableOperators,
}) => {
  const { field, operator, value } = condition;

  // Determine the data type of the currently selected field
  const currentFieldDef = availableFields.find(f => f.value === field);
  const currentFieldType = currentFieldDef?.type;

  // Filter operators based on the selected field's type
  const operatorsForField = React.useMemo(() => {
    if (!currentFieldType) return [];
    return availableOperators[currentFieldType] || [];
  }, [currentFieldType, availableOperators]);

  // Effect to reset operator and value if field changes to an incompatible type
  useEffect(() => {
    if (field && currentFieldType) {
      const compatibleOperator = operatorsForField.find(op => op.value === operator);
      if (!compatibleOperator) {
        // Reset operator and value if current one is not compatible with new field type
        const newFieldDef = availableFields.find(f => f.value === field);
        const defaultOperator = newFieldDef ? (availableOperators[newFieldDef.type] ? availableOperators[newFieldDef.type][0]?.value : '') : '';
        onUpdate({ ...condition, operator: defaultOperator, value: null });
      }
    }
  }, [field, operator, onUpdate, currentFieldType, operatorsForField, condition, availableFields, availableOperators]);


  const handleFieldChange = (newField: string) => {
    const newFieldDef = availableFields.find(f => f.value === newField);
    const defaultOperator = newFieldDef ? (availableOperators[newFieldDef.type] ? availableOperators[newFieldDef.type][0]?.value : '') : '';
    onUpdate({ ...condition, field: newField, operator: defaultOperator, value: null }); // Reset operator and value
  };

  const handleOperatorChange = (newOperator: string) => {
    onUpdate({ ...condition, operator: newOperator });
  };

  const handleValueChange = (newValue: string | number | boolean | Date | string[] | number[] | null | undefined) => {
    onUpdate({ ...condition, value: newValue });
  };

  return (
    // Stacks to a single column on phones (no fixed-width selects overflowing a
    // 360px canvas); becomes an inline row from sm up. Field | Operator | Value.
    <div className="rounded-md border bg-card p-2.5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {/* Field Selection */}
        <Select value={field} onValueChange={handleFieldChange}>
          <SelectTrigger className="w-full sm:w-[10rem] sm:shrink-0">
            <SelectValue placeholder="Select Field" />
          </SelectTrigger>
          <SelectContent>
            {availableFields.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Operator Selection */}
        <Select value={operator} onValueChange={handleOperatorChange} disabled={!field}>
          <SelectTrigger className="w-full sm:w-[10rem] sm:shrink-0">
            <SelectValue placeholder="Select Operator" />
          </SelectTrigger>
          <SelectContent>
            {operatorsForField.map((op) => (
              <SelectItem key={op.value} value={op.value}>
                {op.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Value Input (Dynamically Rendered) */}
        {operator && (
          <div className="w-full sm:flex-1 sm:min-w-[7rem]">
          {currentFieldType === 'text' && (
            <Input
              type="text"
              placeholder="Enter text value"
              value={value !== null && value !== undefined ? String(value) : ''}
              onChange={(e) => handleValueChange(e.target.value)}
            />
          )}
          {currentFieldType === 'number' && operator === 'is_between' && (
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                placeholder="Min"
                value={Array.isArray(value) && value[0] !== undefined && value[0] !== null ? String(value[0]) : ''}
                onChange={(e) => handleValueChange([e.target.value ? parseFloat(e.target.value) : 0, Array.isArray(value) ? value[1] : undefined])}
              />
              <span>and</span>
              <Input
                type="number"
                placeholder="Max"
                value={Array.isArray(value) && value[1] !== undefined && value[1] !== null ? String(value[1]) : ''}
                onChange={(e) => handleValueChange([Array.isArray(value) ? value[0] : undefined, e.target.value ? parseFloat(e.target.value) : 0])}
              />
            </div>
          )}
          {currentFieldType === 'number' && operator !== 'is_between' && (
            <Input
              type="number"
              placeholder="Enter numeric value"
              value={value !== null && value !== undefined ? String(value) : ''}
              onChange={(e) => handleValueChange(e.target.value ? parseFloat(e.target.value) : 0)}
            />
          )}
          {currentFieldType === 'boolean' && (
            <div className="flex items-center space-x-2">
              <Switch
                checked={value === true}
                onCheckedChange={(checked) => handleValueChange(checked)}
              />
              <span>{value ? 'True' : 'False'}</span>
            </div>
          )}
          {currentFieldType === 'timestamp' && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !value && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {value && typeof value === 'string' ? format(new Date(value), "PPP HH:mm") : <span>Pick a date and time</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={value && typeof value === 'string' ? new Date(value) : undefined}
                  onSelect={(date) => handleValueChange(date?.toISOString())} // Store as ISO string
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          )}
          {currentFieldType === 'array_text' && (
            <Input
              type="text"
              placeholder="Enter comma-separated values"
              value={Array.isArray(value) ? value.join(', ') : ''}
              onChange={(e) => handleValueChange(e.target.value.split(',').map(s => s.trim()))}
            />
          )}
          {currentFieldType === 'jsonb' && (
            <Input
              type="text"
              placeholder="Enter value (e.g., JSON string, key, or path)"
              value={value !== null && value !== undefined ? String(value) : ''}
              onChange={(e) => handleValueChange(e.target.value)}
            />
          )}
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={() => onRemove(condition.id)}
          aria-label="Remove condition"
          className="self-end sm:self-auto sm:shrink-0 text-muted-foreground hover:text-status-danger"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default RuleConditionRow;